import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import tastytradeClient from '../integrations/tastytrade/client'; // Corrected import path
import { useBrokerConnections } from '../hooks/use-broker-connections'; // Added import

const TastyTradeCallbackHandler: React.FC = () => {
  debugger; // Pauses execution when the component initially loads/renders
  console.log('[TastyTradeCallbackHandler] Component rendering or re-rendering. Top of function.'); // Added log

  const location = useLocation();
  const navigate = useNavigate();
  const { setBrokerConnected } = useBrokerConnections(); // Added hook usage
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState<string | null>(null);
  const processedLocationSearch = useRef<string | null>(null); // Stores the last processed location.search

  useEffect(() => {
    debugger; // Pauses execution when the useEffect hook runs
    console.log(`[TastyTradeCallbackHandler] TOP OF useEffect. Current location.search: \"${location.search}\". Processed: \"${processedLocationSearch.current}\"`);

    // If location.search is empty or already processed, do nothing further in this effect.
    if (!location.search || location.search === processedLocationSearch.current) {
      console.log(`[TastyTradeCallbackHandler] No new location.search to process (current: \"${location.search}\", processed: \"${processedLocationSearch.current}\"). Ignoring.`);
      return;
    }

    const params = new URLSearchParams(location.search);
    const exchanged = params.get('exchanged');
    const error = params.get('error');
    const errorMessageFromQuery = params.get('message');
    const accessTokenFromQuery = params.get('access_token'); // Read the access token

    // Mark this location.search as processed immediately to prevent re-entry for this specific URL params.
    console.log(`[TastyTradeCallbackHandler] Processing location.search: \"${location.search}\". Marking as processed.`);
    processedLocationSearch.current = location.search;

    if (exchanged === 'true' && accessTokenFromQuery) { // Check for accessTokenFromQuery
      console.log('[TastyTradeCallbackHandler] Detected exchanged=true and access_token.');
      
      console.log(`[TastyTradeCallbackHandler] Calling tastytradeClient.setTastyTradeAccessToken with token: [REDACTED]`);
      tastytradeClient.setTastyTradeAccessToken(accessTokenFromQuery); // Using the client
      
      console.log('[TastyTradeCallbackHandler] Calling setBrokerConnected(\'tastytradeConnected\', true)'); // Added log
      setBrokerConnected('tastytradeConnected', true); // Update shared state

      setStatus('success');
      toast.success('Connected to TastyTrade successfully! Redirecting to portfolio...');
      
      console.log('[TastyTradeCallbackHandler] Navigating to /portfolio.');
      navigate('/portfolio', { replace: true }); 
      return; 

    } else if (error === 'true') {
      console.log('[TastyTradeCallbackHandler] Detected error=true.');
      const finalMessage = errorMessageFromQuery || 'An unknown error occurred during TastyTrade connection.';
      setStatus('error');
      setMessage(finalMessage); 
      toast.error(`TastyTrade Connection Failed: ${finalMessage}. Redirecting...`);
      
      setBrokerConnected('tastytradeConnected', false); // Ensure state is false on error

      console.log('[TastyTradeCallbackHandler] Navigating to /auth-error.');
      navigate('/auth-error', { replace: true }); 
      return; 

    } else {
      // This case handles location.search being present but not matching expected params.
      console.warn(`[TastyTradeCallbackHandler] Loaded with unexpected query parameters: "${location.search}".`);
      setStatus('error');
      setMessage('Invalid callback parameters. Please try connecting again.');
      toast.error('Invalid callback parameters from TastyTrade. Redirecting...');
      
      setBrokerConnected('tastytradeConnected', false); // Ensure state is false on error
      
      console.log('[TastyTradeCallbackHandler] Navigating to /auth-error due to unexpected params.');
      navigate('/auth-error', { replace: true }); 
      return; // Return after navigation
    }
    // No need to update processedLocationSearch.current again here.

  }, [location, navigate, setBrokerConnected]); // Added setBrokerConnected to dependencies

  if (status === 'processing') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
        <div className="w-full max-w-md p-8 space-y-4 bg-white rounded-lg shadow-md">
          <h1 className="text-2xl font-bold text-center text-gray-800">Processing TastyTrade Connection</h1>
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
          <p className="text-center text-gray-600">Finalizing your TastyTrade connection...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
        <div className="w-full max-w-md p-8 space-y-4 bg-white rounded-lg shadow-md">
          <h1 className="text-2xl font-bold text-center text-red-600">Connection Failed</h1>
          <p className="text-center text-gray-600">
            {message || 'There was an error connecting to TastyTrade.'}
          </p>
          <div className="flex justify-center">
            <button 
              onClick={() => navigate('/portfolio', { replace: true })}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Return to Portfolio
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If status is not 'processing' and not 'error', it implies 'success'.
  // This UI will briefly show if status becomes 'success' before navigation completes.
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
      <div className="w-full max-w-md p-8 space-y-4 bg-white rounded-lg shadow-md">
        <h1 className="text-2xl font-bold text-center text-green-600">Connection Successful</h1>
        <p className="text-center text-gray-600">{message || 'Redirecting to your dashboard...'}</p>
        {/* You could add a spinner here too if the redirect takes a moment */}
        {/* <div className="flex justify-center pt-4">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div> */}
      </div>
    </div>
  );
};

export default TastyTradeCallbackHandler;